import * as React from "react";
import { t } from "i18next";
import { DrawerPlmWithConfirmation } from "src/components/DrawerPlmWithConfirmation";
import { Form, Input } from "antd";
import { ImageCarousel } from "src/components/ImageCarousel";
import { FnLoad, TableStore } from "src/components/tables/TableStore";
import { ifDef } from "src/common/ifDef";
import { observer } from "mobx-react-lite";
import { AColumn } from "src/components/tables/AsyncTable";
import { FilterFieldsDict } from "src/components/tables/FiltersForm";
import { filterMonoSearch } from "src/components/tables/FiltersForm/filterMonoSearch";
import { KeepSelected } from "src/components/tables/KeepSelected";
import { ScrollableTable } from "src/components/ScrollableTable";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { onError } from "src/common/onError";
import { loadBom2aColors } from "../../../apiBom2aPositions";
import {
  EdBom2aColorModel,
  FilterBom2aColors,
  ZBom2aColorRow,
} from "../../../Bom2aPositionsTypes";
import styles from "./Bom2aColorModal.module.less";

export type Bom2aColorInfo = {
  colorManual: string | null;
  colorReference: string[];
  comment: string | null;
  color: ZBom2aColorRow | null;
};

interface PropsBom2aColorModal {
  open: boolean;
  close(): void;
  initialData: EdBom2aColorModel;
  save(data: Bom2aColorInfo): Promise<void>;
}

export const Bom2aColorModal: React.FC<PropsBom2aColorModal> = (props) => {
  const { open, close, initialData, save } = props;
  const [form] = Form.useForm<Bom2aColorInfo>();
  const [saving, setSaving] = React.useState(false);
  const [tableStore, setTableStore] = React.useState<TableStore<
    ZBom2aColorRow,
    FilterBom2aColors
  > | null>(null);

  const onSubmit = (values: Bom2aColorInfo) => {
    setSaving(true);
    save(values)
      .then(close)
      .finally(() => setSaving(false))
      .catch(onError);
  };

  React.useEffect(() => {
    if (open) {
      form.resetFields();
      const { bomPositionColorModel: cm } = initialData;
      const srcColorRef = cm.fields.colorReference.value;
      form.setFieldsValue({
        colorManual: cm.fields.colorManual.value,
        colorReference: srcColorRef ? [srcColorRef] : [],
        comment: cm.fields.comment.value,
        color:
          ifDef(cm.color.id, (id) => ({
            id,
            name: cm.color.name.value,
            article: cm.color.article.value,
            hex: cm.color.hex.value || null,
          })) ?? null,
      } satisfies Bom2aColorInfo);
      const store = new TableStore<ZBom2aColorRow, FilterBom2aColors>({
        rowKey: "id",
        fnLoad: colorsLoader,
        selectionSettings: {
          selectionType: "radio",
          keepSelected: true,
        },
      });
      setTableStore(store);
    }
  }, [open]);

  return (
    <DrawerPlmWithConfirmation
      drawerProps={{
        open,
        title: `${t("Color model")}: ${initialData.name}`,
        onClose: close,
      }}
      confirmationProps={{
        okText: t("Select"),
        okButtonProps: {
          onClick: form.submit,
          htmlType: "submit",
          loading: saving,
        },
        cancelButtonProps: {
          onClick: close,
        },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        className={styles.form}
      >
        <div className={styles.firstPart}>
          <div className={styles.firstLeft}>
            <Form.Item name="colorManual" label={t("Color manual")}>
              <Input.TextArea className={styles.textarea} />
            </Form.Item>
            <Form.Item name="comment" label={t("Comment")}>
              <Input.TextArea className={styles.textarea} />
            </Form.Item>
          </div>
          <div className={styles.firstRight}>
            <Form.Item name="colorReference" label={t("Color reference")}>
              <ImageCarousel multiple={false} imageHeight={90} />
            </Form.Item>
          </div>
        </div>
        {!!tableStore && (
          <Form.Item
            name="color"
            label={t("Color")}
            className={styles.colorFormItem}
          >
            <ColorSelect tableStore={tableStore} />
          </Form.Item>
        )}
      </Form>
    </DrawerPlmWithConfirmation>
  );
};

const colorsLoader: FnLoad<ZBom2aColorRow, FilterBom2aColors> = async (
  params,
) => {
  const res = await loadBom2aColors({
    page: params.page,
    size: params.pageSize,
    query: params.filters?.query,
  });
  return {
    rows: res.content,
    totalItems: res.totalElements,
  };
};

interface PropsColorSelect {
  value?: ZBom2aColorRow | null;
  onChange?(newValue?: ZBom2aColorRow | null): void;
  tableStore: TableStore<ZBom2aColorRow, FilterBom2aColors>;
}
const ColorSelect: React.FC<PropsColorSelect> = observer((props) => {
  const { value, onChange, tableStore } = props;
  React.useEffect(() => {
    tableStore.safeSelect(ifDef(value, (v) => [v]) ?? []);
  }, [value]);
  React.useEffect(() => {
    onChange?.(tableStore.selected[0]);
  }, [tableStore.selected]);
  const columns = React.useMemo(
    () =>
      [
        {
          key: "color",
          title: "Color",
          render: (_, { hex }) =>
            hex ? (
              <div
                className={styles.colorBox}
                style={{ background: hex }}
                title={hex}
              />
            ) : null,
        },
        { key: "name", title: "Name", dataIndex: "name" },
        { key: "article", title: "Article", dataIndex: "article" },
      ] satisfies AColumn<ZBom2aColorRow>[],
    [],
  );
  const filter: FilterFieldsDict<FilterBom2aColors> = {
    query: filterMonoSearch(),
  };
  return (
    <div className={styles.tableBox}>
      <KeepSelected store={tableStore} makeTag={({ name }) => name} />
      <ScrollableTable
        store={tableStore}
        columns={columns}
        filterItems={filter}
        onRowClick={(row) => {
          selectOnRowClick(row, tableStore);
        }}
      />
    </div>
  );
});
